import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { TableChart as TableChartIcon } from "@mui/icons-material";
import PaginationControls from "../../../components/ui/PaginationControls";
import { NewUser } from "../../../shared/utils/userValidation";

export interface CsvPreviewProps {
  csvUsers: NewUser[];
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const CsvPreview: React.FC<CsvPreviewProps> = ({
  csvUsers,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();

  const totalPages = Math.ceil(csvUsers.length / rowsPerPage);
  const paginatedUsers = csvUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[400]}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.1),
            color: "info.main",
          }}
        >
          <TableChartIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            CSV Preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {csvUsers.length} users ready to import
          </Typography>
        </Box>
      </Box>

      <TableContainer
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[400]}`,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                "& .MuiTableCell-head": {
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                },
              }}
            >
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Badge Number</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Bodycam Vendor ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user, index) => (
              <TableRow
                key={`${user.email}-${index}`}
                sx={{
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.info.main, 0.04),
                  },
                  "&:nth-of-type(even)": {
                    bgcolor: alpha(theme.palette.action.hover, 0.02),
                  },
                }}
              >
                <TableCell>{user.firstName}</TableCell>
                <TableCell>{user.lastName}</TableCell>
                <TableCell>{user.position}</TableCell>
                <TableCell>{user.badgeNumber}</TableCell>
                <TableCell>{user.department}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.bodycamVendorId || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.400",
          bgcolor: "background.paper",
        }}
      >
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          totalItems={csvUsers.length}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
    </Paper>
  );
};

export default CsvPreview;
